import { DonationProvider } from '@joindeed/calculate-fees';
import { Record, List } from 'immutable';
import pick from 'lodash/pick';

import { CurrencyCode, AmountCurrencies } from 'utils/currencyFormat';
import { estimateCorporateDonationDisbursedAt } from 'utils/estimateCorporateDonationDisbursedAt';
import { estimateCorporateDonationProcessedAt } from 'utils/estimateCorporateDonationProcessedAt';
import { getDonationProviderDisplayName } from 'utils/getDonationProviderDisplayName';
import DonationSchedule from 'entities/donationSchedule/model';
import { executionErrorMessages } from 'entities/donation/executionErrorMessages';

import Deed from '../deed/model';
import User from '../user/model';
import Organizer from '../organizer/model';
import Organization from '../organization/model';
import VolunteerTime from '../volunteerTime/model';

interface PaymentMethodDetails {
  type: string;
  additionalData: Map<string, any>;
}

interface PayoutBatch {
  _id: string;
  createdAt: Date;
  processedAt: Date;
}

interface Metadata {
  matchRequestRejectedAt?: Date;
  matchRequestRejectionReason?: string;
}

interface PayrollDeduction {
  employeePayrollRun: string;
  name: string;
  employeeDeduction: number;
  companyDeduction: number;
  currency: string;
  remoteData?: Record<string, unknown>;
}

export interface MatchableRule {
  budget: string;
  bypassBudgetLimitPerUser: boolean;
  id: string;
  matchingPercentage: number;
  paymentMethods: string[];
  paymentMethodsExclude: boolean;
  title: string;
  matchableAmount?: number;
  matchingMaximumCurrencies?: AmountCurrencies;
  matchingMaximumInDonationCurrency?: number;
}

export interface MatchableRules {
  [key: string]: MatchableRule;
}

export enum PayrollRunState {
  PAID = 'PAID',
  DRAFT = 'DRAFT',
  IN_PROGRESS = 'IN_PROGRESS',
  APPROVED = 'APPROVED',
  FAILED = 'FAILED',
  CLOSED = 'CLOSED',
}

export interface PayrollRun {
  _id: string;
  id: string;
  deduction?: PayrollDeduction[];
  endDate: Date;
  payDate?: Date;
  runState: PayrollRunState;
  startDate: Date;
}

export interface DonationReceipt {
  confidenceScore?: number;
  reasoningNote?: string;
  userReceiptDataOverrides?: {
    [key: string]: unknown;
  };
  parsedReceiptData?: {
    [key: string]: unknown;
  };
}

const properties = {
  type: null,
  id: '',
  // @NOTE-RT: Ugh. This will be problematic – we should never default an empty user...
  user: new User(),
  submitter: null,
  deed: new Deed(),
  nonprofits: List(),
  corporateProgram: '',
  identifier: '',
  receiptNumber: '',
  receiptUrl: '',
  description: '',
  amount: 0,
  total: undefined,
  feeAmount: undefined,
  currency: '',
  amountCurrencies: {},
  createdAt: new Date(),
  date: new Date(),
  matchableRule: undefined,
  matchableRules: undefined,
  payoutBatch: null,
  amountToMatch: undefined,
  matchableAmount: undefined,
  desiredMatchAmount: undefined,
  desiredMatchingRule: undefined,
  matchableAmountCurrencies: {},
  matchingMaximumCurrencies: {},
  matchingError: '',
  provider: '',
  matchingProvider: '',
  status: '',
  matchingRequestStatus: '',
  matchingBudgetId: '',
  // DEPRECATED
  matchedAmount: 0,
  matchedAmountCurrencies: 0,
  matchedRule: '',
  matchedAt: undefined,
  paymentMethodDetails: {},
  paymentMethodDetailsType: '',
  disbursedAt: undefined,
  matchDisbursedAt: undefined,
  processedAt: undefined,
  matchProcessedAt: undefined,
  imported: false,
  metadata: undefined,
  donationSchedule: undefined,
  note: undefined,
  employeeNote: undefined,
  designation: undefined,
  dedication: undefined,
  budgetAllocation: undefined,
  payrollStatus: '',
  payrollRun: undefined,
  executionFailure: undefined,
  rescheduledDonation: undefined,
  coveredFeeDisbursementInstruction: undefined,
  coveredFeeDisbursementTransactionIdentifier: undefined,
  coveredFeeDisbursementTransactionUrl: undefined,
  donationDisbursementInstruction: undefined,
  donationDisbursementTransactionIdentifier: undefined,
  donationDisbursementTransactionUrl: undefined,
  matchDisbursementInstruction: undefined,
  matchDisbursementTransactionIdentifier: undefined,
  matchDisbursementTransactionUrl: undefined,
  volunteerTime: new VolunteerTime(),
  donationReceipt: undefined,
};

export interface ExecutionFailure {
  errorCode:
    | 'Deduction_failed'
    | 'Deduction_retrying'
    | 'Deduction_missing'
    | 'Deduction_value_mismatch'
    | 'Deduction_orphaned'
    | 'Deduction_event_creation_failed';
  errorMessage: string;
  date: Date;
}

export default class Donation extends Record(properties, 'Donation') {
  public readonly type!: string | null;

  public readonly id!: string;

  public readonly user!: User;

  public readonly submitter!: Organizer;

  public readonly deed!: Deed;

  public readonly nonprofits!: List<Organization>;

  public readonly corporateProgram?: string;

  public readonly identifier!: string;

  public readonly receiptNumber!: string;

  public readonly receiptUrl!: string;

  public readonly description!: string;

  public readonly amount!: number;

  public readonly total?: number;

  public readonly feeAmount?: number;

  public readonly currency!: CurrencyCode;

  public readonly amountCurrencies!: AmountCurrencies;

  public readonly createdAt!: Date;

  public readonly date!: Date;

  public readonly matchableRule!: MatchableRule;

  public readonly matchableRules?: MatchableRules;

  public readonly payoutBatch!: PayoutBatch | null;

  public readonly amountToMatchCurrencies!: any;

  public readonly matchingProvider!: string;

  public readonly status!: 'Success' | 'Pending';

  public readonly matchingRequestStatus!: 'Approved' | 'Rejected' | 'Pending';

  public readonly matchedAmount!: number;

  public readonly matchableAmount!: number;

  public readonly desiredMatchAmount!: number;

  public readonly desiredMatchingRule?: string;

  public readonly matchedAmountCurrencies!: AmountCurrencies;

  public readonly matchableAmountCurrencies!: AmountCurrencies;

  public readonly matchingMaximumCurrencies!: AmountCurrencies;

  public readonly matchingBudgetId!: string;

  public readonly matchedRule!: string;

  public readonly matchedAt!: Date;

  public readonly paymentMethodDetails!: PaymentMethodDetails;

  public readonly paymentMethodDetailsType!: string;

  public readonly disbursedAt!: Date;

  public readonly processedAt!: Date;

  public readonly matchProcessedAt!: Date;

  public readonly imported!: boolean;

  public readonly metadata!: Metadata;

  public readonly note!: string;

  public readonly employeeNote?: string;

  public readonly designation!: string;

  public readonly dedication!: string;

  public readonly budgetAllocation!: string;

  public readonly payrollStatus!: string;

  public readonly payrollRun?: PayrollRun;

  public readonly executionFailure!: ExecutionFailure;

  public readonly donationSchedule?: DonationSchedule;

  public readonly rescheduledTo?: string;

  public readonly volunteerTime!: VolunteerTime;

  // START disbursement instruction
  public readonly coveredFeeDisbursedAt?: Date;

  public readonly coveredFeeDisbursementInstruction?: string;

  public readonly coveredFeeDisbursementTransactionIdentifier?: string;

  public readonly coveredFeeDisbursementTransactionUrl?: string;

  public readonly donationDisbursedAt?: Date;

  public readonly donationDisbursementInstruction?: string;

  public readonly donationDisbursementTransactionIdentifier?: string;

  public readonly donationDisbursementTransactionUrl?: string;

  public readonly donationReceipt?: DonationReceipt;

  public readonly matchDisbursedAt?: Date;

  public readonly matchDisbursementInstruction?: string;

  public readonly matchDisbursementTransactionIdentifier?: string;

  public readonly matchDisbursementTransactionUrl?: string;
  // END disbursement instruction

  constructor(valuesArgument: any) {
    const values = valuesArgument || {};
    const validValues = pick(values, Object.keys(properties));
    super({
      ...validValues,
      id: values.id ?? values._id,
      type: values.__t,
      user:
        values.user &&
        new User({
          id: values.user._id,
          ...values.user,
        }),
      submitter:
        values.submitter &&
        new Organizer({
          id: values.submitter._id,
          ...values.submitter,
        }),
      deed: values.deed && new Deed(values.deed),
      date: values.date ? new Date(values.date) : undefined,
      createdAt: values.createdAt ? new Date(values.createdAt) : undefined,
      matchedAt: values.matchedAt ? new Date(values.matchedAt) : undefined,
      currency: values?.currency?.toUpperCase(),
      nonprofits:
        values.nonprofits &&
        List(values.nonprofits.map((nonprofit: Organization) => new Organization(nonprofit))),
      corporateProgram: values.corporateProgramId?.name,
      payoutBatch: values.payoutBatch
        ? {
            _id: values.payoutBatch._id,
            createdAt: new Date(values.payoutBatch.createdAt),
            processedAt: new Date(values.payoutBatch.processedAt),
          }
        : null,
      disbursedAt: values.disbursedAt ? new Date(values.disbursedAt) : undefined,
      coveredFeeDisbursedAt: values.coveredFeeDisbursedAt
        ? new Date(values.coveredFeeDisbursedAt)
        : undefined,
      donationDisbursedAt: values.donationDisbursedAt
        ? new Date(values.donationDisbursedAt)
        : undefined,
      matchDisbursedAt: values.matchDisbursedAt ? new Date(values.matchDisbursedAt) : undefined,
      processedAt: values.processedAt ? new Date(values.processedAt) : undefined,
      matchProcessedAt: values.matchProcessedAt ? new Date(values.matchProcessedAt) : undefined,
      metadata: values.metadata
        ? {
            matchRequestRejectedAt: values.metadata.matchRequestRejectedAt
              ? new Date(values.metadata.matchRequestRejectedAt)
              : undefined,
            matchRequestRejectionReason: values.metadata.matchRequestRejectionReason,
          }
        : undefined,
      donationSchedule: values.donationSchedule,
    });
  }

  get deedName() {
    return (
      this?.deed?.name ||
      (this.imported ? 'Imported donation' : '') ||
      (this.isExternalMatchRequest() ? 'External match' : '')
    );
  }

  get provider(): DonationProvider {
    return this.get('provider');
  }

  get providerName(): string {
    if (this.imported) {
      return 'Imported';
    }
    if (this.isExternal()) {
      return 'External';
    }
    return getDonationProviderDisplayName(this.provider) || '';
  }

  get matchRequestRejectedAt() {
    return this.metadata?.matchRequestRejectedAt;
  }

  get matchRequestRejectionReason() {
    return this.metadata?.matchRequestRejectionReason;
  }

  get estimatedDisbursedAt() {
    if (this.disbursedAt) {
      return null;
    }

    if (this.isCorporateBudget()) {
      return estimateCorporateDonationDisbursedAt(
        this.provider,
        this.nonprofits?.first(),
        this.processedAt ?? this.estimatedProcessedAt
      );
    }

    return undefined;
  }

  get estimatedProcessedAt() {
    if (this.processedAt) {
      return null;
    }

    if (this.isCorporateBudget()) {
      return estimateCorporateDonationProcessedAt(this.provider);
    }

    return undefined;
  }

  get donationTrackingURL() {
    if (this.isExternal()) {
      return null;
    }
    return this.donationDisbursementTransactionUrl ?? this.receiptUrl;
  }

  get matchTrackingURL() {
    return this.matchDisbursementTransactionUrl;
  }

  isByUser(user: User): boolean {
    const userId = typeof user === 'string' ? user : user?.id;
    return this.user?.id === userId;
  }

  isForDeed(deed: Deed): boolean {
    const deedId = typeof deed === 'string' ? deed : deed?.id;
    return this.deed.id === deedId;
  }

  isExternal(): boolean {
    return this.paymentMethodDetails?.type === 'External' || this.provider === 'External';
  }

  isCorporateDonation(): boolean {
    return this.type === 'CorporateDonation';
  }

  isExternalMatchRequest(): boolean {
    return !this.imported && this.paymentMethodDetails?.type === 'External';
  }

  isDeedCredit(): boolean {
    return this.paymentMethodDetails?.type === 'DeedCredit';
  }

  isPayroll(): boolean {
    return this.paymentMethodDetails?.type === 'Payroll';
  }

  isCorporateBudget(): boolean {
    return this.paymentMethodDetails?.type === 'CorporateBudget';
  }

  getExecutionErrorMessage(): string | undefined {
    return executionErrorMessages[this.executionFailure?.errorCode];
  }
}
